/**
 * QuickBooks Online API client (frontend).
 */

import { supabase } from './supabase';

const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

async function authHeader(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${session.access_token}` };
}

export interface QboSummary {
  customers: number;
  ar_total_cents: number;
  ar_buckets: {
    current: number;
    b0_30: number;
    b31_60: number;
    b61_90: number;
    b90plus: number;
  };
  open_count: number;
  overdue_count: number;
  revenue_30d_cents: number;
  revenue_90d_cents: number;
  /** Mean invoice date → paid, last 180d; null with no paid invoices. */
  avg_collection_days: number | null;
  top_overdue: Array<{
    balance_cents: number;
    days_overdue: number | null;
    customer_external_id: string | null;
    doc_number: string | null;
    due_date: string | null;
  }>;
}

export async function getQboAuthUrl(returnTo: string, mode: 'sandbox' | 'production' = 'production'): Promise<string> {
  const headers = await authHeader();
  const qs = new URLSearchParams({ returnTo, mode });
  const res = await fetch(`${API_URL}/v1/integrations/quickbooks/connect?${qs}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string; hint?: string };
    throw new Error(body.hint || body.error || `API ${res.status}`);
  }
  const data = (await res.json()) as { authUrl: string };
  return data.authUrl;
}

export async function disconnectQbo(): Promise<void> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/integrations/quickbooks`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${(await res.text()).slice(0, 200)}`);
}

export async function syncQboNow(): Promise<{
  customersUpserted: number;
  invoicesUpserted: number;
  overdueInvoices: number;
  signalsCreated: number;
}> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/integrations/quickbooks/sync`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string; detail?: string };
    throw new Error(body.detail || body.error || `API ${res.status}`);
  }
  return (await res.json()) as {
    customersUpserted: number;
    invoicesUpserted: number;
    overdueInvoices: number;
    signalsCreated: number;
  };
}

export async function getQboSummary(): Promise<QboSummary> {
  const headers = await authHeader();
  const res = await fetch(`${API_URL}/v1/integrations/quickbooks/summary`, { headers });
  if (!res.ok) throw new Error(`API ${res.status}: ${(await res.text()).slice(0, 200)}`);
  return (await res.json()) as QboSummary;
}
